import { Form, Button, Row, Col, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { Container } from '../../../components/ui/Container/Container';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import useClubMemberAddSearch from './useClubMemberAddSearch';
import { Helpers } from '../../../utils/helpers';
import { DefaultLayout as Layout } from '../../../layouts/Default/DefaultLayout';

// Interface for flattened member display
interface MemberDisplayRow {
  clubMemberId: string;
  memberId: string;
  memberType: 'Primary' | 'Secondary';
  firstName: string;
  lastName: string;
  middleName: string;
  emailAddress: string;
  dateOfBirthUtc: Date | null;
  cellPhone: string;
  homePhone: string;
  statusDescription: string;
  statusCodeValue: string;
}

export const ClubMemberAddSearch = () => {
  const manager = useClubMemberAddSearch();
  const ToastContainer = manager.ToastContainer;

  // Handle Enter key press to trigger search
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      manager.performSearch();
    }
  };

  // Helper function to check if a member matches the search criteria
  const memberMatchesSearch = (
    firstName: string,
    lastName: string,
    dateOfBirth: Date | null
  ): boolean => {
    const firstNameMatch = firstName.toLowerCase() === manager.searchParams.firstName.toLowerCase();
    const lastNameMatch = lastName.toLowerCase() === manager.searchParams.lastName.toLowerCase();

    let dobMatch = false;
    if (dateOfBirth && manager.searchParams.birthDate) {
      const memberDob = Helpers.toDateString(dateOfBirth);
      dobMatch = memberDob === manager.searchParams.birthDate;
    }

    return firstNameMatch && lastNameMatch && dobMatch;
  };

  // Flatten the search results to include only members that match the search criteria
  const flattenedResults: MemberDisplayRow[] = [];
  if (manager.searchResults.results) {
    manager.searchResults.results.forEach((clubMember) => {
      // Check if primary member matches search criteria
      const primaryMatches = memberMatchesSearch(
        clubMember.primaryMember.firstName,
        clubMember.primaryMember.lastName,
        clubMember.primaryMember.dateOfBirthUtc
      );

      if (primaryMatches) {
        flattenedResults.push({
          clubMemberId: clubMember.id,
          memberId: clubMember.primaryMember.id,
          memberType: 'Primary',
          firstName: clubMember.primaryMember.firstName,
          lastName: clubMember.primaryMember.lastName,
          middleName: clubMember.primaryMember.middleName,
          emailAddress: clubMember.primaryMember.emailAddress,
          dateOfBirthUtc: clubMember.primaryMember.dateOfBirthUtc,
          cellPhone: clubMember.primaryMember.cellPhone,
          homePhone: clubMember.primaryMember.homePhone,
          statusDescription: clubMember.primaryMember.memberStatus?.description || 'Unknown',
          statusCodeValue: clubMember.primaryMember.memberStatus?.codeValue || ''
        });
      }

      // Check if secondary member exists and matches search criteria
      if (clubMember.secondaryMember) {
        const secondaryMatches = memberMatchesSearch(
          clubMember.secondaryMember.firstName,
          clubMember.secondaryMember.lastName,
          clubMember.secondaryMember.dateOfBirthUtc
        );

        if (secondaryMatches) {
          flattenedResults.push({
            clubMemberId: clubMember.id,
            memberId: clubMember.secondaryMember.id,
            memberType: 'Secondary',
            firstName: clubMember.secondaryMember.firstName,
            lastName: clubMember.secondaryMember.lastName,
            middleName: clubMember.secondaryMember.middleName,
            emailAddress: clubMember.secondaryMember.emailAddress,
            dateOfBirthUtc: clubMember.secondaryMember.dateOfBirthUtc,
            cellPhone: clubMember.secondaryMember.cellPhone,
            homePhone: clubMember.secondaryMember.homePhone,
            statusDescription: clubMember.secondaryMember.memberStatus?.description || 'Unknown',
            statusCodeValue: clubMember.secondaryMember.memberStatus?.codeValue || ''
          });
        }
      }
    });
  }

  // Check if any member has ACTIVE or NEW status
  // Only show "Add New Member" button if NO members have these statuses
  const hasActiveOrNewMember = flattenedResults.some(
    (member) => member.statusCodeValue === 'ACTIVE' || member.statusCodeValue === 'NEW'
  );
  const canAddNewMember =
    manager.hasSearched && (flattenedResults.length === 0 || !hasActiveOrNewMember);

  return (
    <Layout>
      <ToastContainer />
      <h1 id="primaryPageTitle" className="d-inline mb-4">
        Add Club Member
      </h1>
      <Container className="page-container mt-2">
        <div className="card mb-4">
          <div className="card-header bg-light d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Search for Existing Member</h5>
            {canAddNewMember && (
              <Button variant="success" onClick={manager.addNewMember}>
                <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                Add New Member
              </Button>
            )}
          </div>
          <div className="card-body">
            <p className="text-muted mb-3">
              Before adding a new member, please search to ensure they dont already exist in the
              system.
            </p>

            <Form>
              <Row className="g-3 align-items-end">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="required">Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={manager.searchParams.lastName}
                      onChange={(e) => manager.updateSearchParam('lastName', e.target.value)}
                      placeholder="Enter last name"
                      onKeyDown={handleKeyDown}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="required">First Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={manager.searchParams.firstName}
                      onChange={(e) => manager.updateSearchParam('firstName', e.target.value)}
                      placeholder="Enter first name"
                      onKeyDown={handleKeyDown}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="required">Birth Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={manager.searchParams.birthDate}
                      onChange={(e) => manager.updateSearchParam('birthDate', e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Button
                    variant="primary"
                    onClick={manager.performSearch}
                    disabled={manager.isSearching}
                    className="w-100">
                    {manager.isSearching ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSearch} className="me-2" />
                        Search
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </Form>
          </div>
        </div>

        <div className="card">
          <div className="card-header bg-light">
            <h5 className="mb-0">Search Results</h5>
          </div>
          <div className="card-body">
            <LoadingContainer isLoading={manager.isSearching}>
              {!manager.hasSearched && (
                <div className="text-center py-4">
                  <p className="mb-0">Enter search criteria and click Search to find members.</p>
                </div>
              )}

              {manager.hasSearched && flattenedResults.length === 0 && (
                <div className="text-center py-4">
                  <p className="mb-0">No existing members found with this criteria.</p>
                  <p className="text-muted">
                    You can add a new member using the button at the top.
                  </p>
                </div>
              )}

              {manager.hasSearched && flattenedResults.length > 0 && (
                <>
                  {!hasActiveOrNewMember && (
                    <div className="alert alert-info mb-3">
                      <strong>Note:</strong> Found existing inactive members. You can review them or
                      create a new member record.
                    </div>
                  )}
                  {hasActiveOrNewMember && (
                    <div className="alert alert-warning mb-3">
                      <strong>Warning:</strong> Found existing members with Active or New Candidate
                      status. Please select an existing member instead of creating a duplicate.
                    </div>
                  )}
                  <Table hover responsive>
                    <thead>
                      <tr>
                        <th>Member Type</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Birth Date</th>
                        <th>Phone</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flattenedResults.map((member, index) => (
                        <tr key={`${member.clubMemberId}-${member.memberType}-${index}`}>
                          <td>
                            <span
                              className={`badge ${member.memberType === 'Primary' ? 'bg-primary' : 'bg-secondary'}`}>
                              {member.memberType}
                            </span>
                          </td>
                          <td>
                            {member.firstName} {member.lastName}
                            {member.middleName && ` ${member.middleName}`}
                          </td>
                          <td>{member.emailAddress}</td>
                          <td>{Helpers.toDateString(member.dateOfBirthUtc)}</td>
                          <td>{member.cellPhone || member.homePhone}</td>
                          <td>{member.statusDescription}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => manager.selectExistingMember(member.clubMemberId)}>
                              View Member
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </LoadingContainer>
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default ClubMemberAddSearch;
